import { useNavigate } from '@tanstack/react-router';
import { AutocompleteSingleSelect } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { useOwnedSavedReportsQuery, useSharedSavedReportsQuery } from '../../hooks';
import { SavedReportSource } from '../../model';

interface SavedReportPresetSelectProps {
  agencyId?: string | undefined;
  savedReportId?: string | undefined;
  savedReportSource?: SavedReportSource | undefined;
}

const separator = ':';

/** Loads a saved report into Report Explorer through the same route search as "Run". */
export function SavedReportPresetSelect({
  agencyId,
  savedReportId,
  savedReportSource,
}: SavedReportPresetSelectProps) {
  const navigate = useNavigate();
  const owned = useOwnedSavedReportsQuery(true, agencyId);
  const shared = useSharedSavedReportsQuery(true, agencyId);
  const byName = (left: { name: string }, right: { name: string }) =>
    left.name.localeCompare(right.name);
  const options = [
    ...[...(owned.data ?? [])].sort(byName).map((report) => ({
      id: [SavedReportSource.Owned, report.id].join(separator),
      label: report.name,
    })),
    ...[...(shared.data ?? [])].sort(byName).map((report) => ({
      id: [SavedReportSource.Shared, report.id].join(separator),
      label: dashboardT('reportExplorer.presets.shared', { name: report.name }),
    })),
  ];
  const value =
    savedReportId && savedReportSource ? [savedReportSource, savedReportId].join(separator) : '';

  return (
    <AutocompleteSingleSelect
      className="w-64"
      label={dashboardT('reportExplorer.presets.label')}
      placeholder={dashboardT('reportExplorer.presets.placeholder')}
      searchPlaceholder={dashboardT('reportExplorer.presets.search')}
      emptyMessage={dashboardT('reportExplorer.presets.empty')}
      options={options}
      value={value}
      loading={owned.isFetching || shared.isFetching}
      onValueChange={(next) => {
        const [source, id] = next.split(separator);
        const preset =
          id && (source === SavedReportSource.Owned || source === SavedReportSource.Shared)
            ? { savedReportId: id, savedReportSource: source }
            : null;
        // Clearing returns to a new, default report on the same route.
        if (!preset && next) return;
        void navigate({
          to: agencyId ? '/a/$agencyId/reporting/report-explorer' : '/reporting/report-explorer',
          params: { agencyId: agencyId ?? '' },
          search: preset ?? {},
        });
      }}
    />
  );
}
